import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { getLineItemDisplay } from '@/lib/line-item-display'
import { cn } from '@/lib/utils'
import { ChevronDown, ImageIcon, ShoppingBasket } from 'lucide-react'
import { useState } from 'react'

export function getDeliveryRouteOrderItems(order) {
  return Array.isArray(order?.sales_order_items) ? order.sales_order_items : []
}

export function getDeliveryRouteOrderItemQuantity(item) {
  const quantity = Number(item?.quantity)

  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1
}

export function getDeliveryRouteOrderItemCount(items) {
  return items.reduce((total, item) => total + getDeliveryRouteOrderItemQuantity(item), 0)
}

function getDeliveryRouteOrderItemKey(item, fallbackIndex) {
  return item?.id ?? `${item?.variant_details?.name ?? 'item'}-${fallbackIndex}`
}

function getFirstMedia(media) {
  if (Array.isArray(media)) {
    return media.find((item) => item?.url) ?? media[0] ?? null
  }

  return media ?? null
}

function getDeliveryRouteOrderItemImage(item, display) {
  return (
    getFirstMedia(display.image) ??
    getFirstMedia(item?.variant?.media) ??
    getFirstMedia(item?.variant?.product?.media) ??
    null
  )
}

export function DeliveryRouteOrderItemsList({
  className,
  id,
  onClick,
  order,
  testId = 'delivery-route-order-items',
}) {
  const orderItems = getDeliveryRouteOrderItems(order)

  if (orderItems.length === 0) {
    return null
  }

  return (
    <div id={id} data-testid={testId} className={cn('space-y-2', className)} onClick={onClick}>
      {orderItems.map((item, index) => {
        const display = getLineItemDisplay(item)
        const media = getDeliveryRouteOrderItemImage(item, display)
        const quantity = getDeliveryRouteOrderItemQuantity(item)

        return (
          <div
            key={getDeliveryRouteOrderItemKey(item, index)}
            className="flex min-w-0 items-center gap-2"
          >
            {media?.url ? (
              <img
                src={media.url}
                alt={display.primaryName}
                className="size-10 shrink-0 rounded-md bg-muted object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                <ImageIcon className="size-4" />
              </div>
            )}
            <div className="min-w-0 flex-1 space-y-1">
              <Typography variant="caption" tone="default" className="block truncate">
                {display.primaryName}
              </Typography>
              <div className="flex flex-wrap items-center gap-1.5">
                {display.badgeLabel ? (
                  <Badge variant="outline" size="xs">
                    {display.badgeLabel}
                  </Badge>
                ) : null}
                <Typography variant="caption">Qty {quantity}</Typography>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function stopEventPropagation(event) {
  event.stopPropagation()
}

function DeliveryRouteOrderItemsDisclosure({
  buttonClassName,
  className,
  order,
  panelClassName,
  panelId,
  stopPropagation = false,
}) {
  const [isItemsOpen, setIsItemsOpen] = useState(false)
  const orderItems = getDeliveryRouteOrderItems(order)
  const orderItemCount = getDeliveryRouteOrderItemCount(orderItems)
  const orderItemLabel = orderItemCount === 1 ? 'Item' : 'Items'
  const itemsPanelId = panelId ?? `delivery-route-order-${order?.id}-items`

  if (orderItems.length === 0) {
    return null
  }

  const handleItemsToggle = (event) => {
    if (stopPropagation) {
      event.stopPropagation()
    }

    setIsItemsOpen((currentValue) => !currentValue)
  }

  return (
    <div className={className}>
      <Button
        variant="outline"
        size="mini"
        className={buttonClassName}
        aria-expanded={isItemsOpen}
        aria-controls={itemsPanelId}
        onClick={handleItemsToggle}
      >
        <ShoppingBasket className="size-3.5" />
        {orderItemLabel} ({orderItemCount})
        <ChevronDown className={cn('size-3 transition-transform', isItemsOpen && 'rotate-180')} />
      </Button>

      {isItemsOpen ? (
        <DeliveryRouteOrderItemsList
          id={itemsPanelId}
          order={order}
          className={cn('mt-2 border-t border-border/70 pt-2', panelClassName)}
          onClick={stopPropagation ? stopEventPropagation : undefined}
        />
      ) : null}
    </div>
  )
}

export default DeliveryRouteOrderItemsDisclosure
